// Two Pathways. One Movement. — shared explainer distinguishing Global
// Ambassadors (individual leadership) from Partners (organizational impact).
//
// 2026-07-21 · Consolidation pass (user feedback: "cards are not
// collaborated"). Added: (a) a visual '&' connector between the two cards
// so they read as ONE ecosystem rather than two silos, (b) a prominent
// "Do both" CTA banner at the bottom, and (c) cross-links so an
// applicant on one path can jump to the other in one click.
import { Link } from "react-router-dom";
import { Users2, Building2, ArrowRight, HeartHandshake } from "lucide-react";

const PATHS = [
  {
    key: "ambassadors",
    icon: Users2,
    kicker: "Individual leadership",
    title: "Global Ambassadors",
    body: "Individuals who lead through personal example — they sign, act, share, inspire, and grow awareness in their community, profession, city, or country.",
    cta: "Become a Global Ambassador",
    to: "/ambassadors",
  },
  {
    key: "partners",
    icon: Building2,
    kicker: "Organizational collaboration",
    title: "Partners",
    body: "Organizations that multiply impact through collective action — mobilizing teams, students, employees, members, communities, and networks.",
    cta: "Become a DOCM Partner",
    to: "/partners",
  },
];

export default function TwoPathways() {
  return (
    <section
      data-testid="two-pathways-section"
      className="relative py-16 md:py-20 bg-docm-ink text-docm-cream overflow-hidden"
    >
      <div className="pointer-events-none absolute -right-40 -top-40 w-[500px] h-[500px] rounded-full border border-docm-gold/10" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-32 -bottom-32 w-[420px] h-[420px] rounded-full border border-docm-gold/8" aria-hidden="true" />

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-10">
        <div className="max-w-2xl">
          <span className="label-eyebrow text-docm-gold">Two Pathways. One Movement.</span>
          <h2 className="mt-4 h-display text-3xl md:text-4xl leading-tight">
            Join as a person. Lead as a Global Consciousness Ambassador.{" "}
            <span className="font-display italic text-docm-gold">Collaborate as a Partner.</span>
          </h2>
        </div>

        {/* Cards + centre-connector. On desktop the two cards flank a
            small circular '&' node that visually stitches them together.
            On mobile the connector collapses into a vertical bar so the
            layout still reads as "these belong together". */}
        <div className="mt-10 relative">
          {/* Desktop connector — thin gold line + centre '&' badge */}
          <div className="hidden md:block absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-px bg-gradient-to-r from-transparent via-docm-gold/50 to-transparent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 relative">
            {PATHS.map(({ key, icon: Icon, kicker, title, body, cta, to }) => (
              <div
                key={key}
                data-testid={`tp-card-${key}`}
                className="rounded-3xl bg-docm-cream/5 border border-docm-cream/15 p-7 md:p-8 backdrop-blur-sm transition-all duration-500 hover:border-docm-gold/40 hover:-translate-y-1"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-docm-gold/15 border border-docm-gold/30 text-docm-gold mb-4">
                  <Icon size={19} />
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-docm-gold font-semibold">
                  {kicker}
                </div>
                <h3 className="mt-2 font-serif-h text-xl md:text-2xl text-docm-cream">{title}</h3>
                <p className="mt-3 text-[14px] text-docm-cream/75 leading-relaxed">{body}</p>
                <Link
                  to={to}
                  data-testid={`tp-${key}-cta`}
                  className="mt-6 inline-flex items-center gap-2 text-[12.5px] uppercase tracking-[0.16em] font-semibold text-docm-gold hover:text-docm-cream transition-colors group"
                >
                  {cta}
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            ))}
          </div>

          {/* Centre '&' badge — sits ABOVE the cards on both breakpoints */}
          <div
            data-testid="tp-connector-badge"
            aria-hidden="true"
            className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 rounded-full bg-docm-ink border border-docm-gold/60 shadow-[0_10px_28px_-6px_rgba(199,150,52,0.35)]"
          >
            <span className="font-display italic text-docm-gold text-xl leading-none">&amp;</span>
          </div>
        </div>

        {/* Do-both CTA banner — the moment where the two pathways
            explicitly merge for a single leader/organisation. */}
        <div
          data-testid="tp-do-both-banner"
          className="mt-8 md:mt-10 rounded-3xl bg-gradient-to-r from-docm-gold/12 via-docm-cream/5 to-docm-gold/12 border border-docm-gold/25 p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
        >
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-docm-gold/20 border border-docm-gold/40 text-docm-gold shrink-0">
            <HeartHandshake size={18} />
          </div>
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-docm-gold font-semibold">
              You can be both
            </div>
            <p className="mt-1.5 font-display italic text-base md:text-lg text-docm-cream/85 leading-snug">
              An entrepreneur, educator, artist, or public leader may become both — an Ambassador as an
              individual, <span className="text-docm-gold">and</span> a Partner through their organization,
              institution, foundation, or community.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:shrink-0">
            <Link
              to="/ambassadors"
              data-testid="tp-do-both-ambassador"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-docm-gold/50 text-[11.5px] uppercase tracking-[0.16em] font-semibold text-docm-gold hover:bg-docm-gold hover:text-docm-ink transition-colors whitespace-nowrap"
            >
              Apply as Ambassador
            </Link>
            <Link
              to="/partners"
              data-testid="tp-do-both-partner"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-docm-gold/50 text-[11.5px] uppercase tracking-[0.16em] font-semibold text-docm-gold hover:bg-docm-gold hover:text-docm-ink transition-colors whitespace-nowrap"
            >
              Apply as Partner
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
